import { useEffect, useState } from "react";

import type { ClipTextSpan, ClipTipCard } from "@shared/clipSidecar";

const DDRAGON = "https://ddragon.leagueoflegends.com";

type ParsedTipId =
  | { kind: "item"; id: number; quest: boolean }
  | { kind: "spell"; id: number }
  | { kind: "rune"; id: number; role: "keystone" | "secondary" | null };

type ItemJson = {
  data?: Record<
    string,
    { name?: string; description?: string; gold?: { base?: number; total?: number } }
  >;
};

type SummonerJson = {
  data?: Record<string, { key?: string; name?: string; description?: string }>;
};

type RuneStyle = {
  id?: number;
  name?: string;
  slots?: {
    runes?: { id?: number; name?: string; shortDesc?: string }[];
  }[];
};

type PatchAssets = {
  items: ItemJson;
  summoner: SummonerJson;
  runes: RuneStyle[];
};

const patchCache = new Map<string, Promise<PatchAssets>>();
const tipCache = new Map<string, ClipTipCard | null>();

export const parseClipTipId = (tipId: string): ParsedTipId | null => {
  const parts = tipId.split(":");
  const kind = parts[0];
  const id = Number(parts[1]);
  if (!Number.isFinite(id) || id <= 0) return null;
  if (kind === "item") return { kind: "item", id, quest: parts[2] === "quest" };
  if (kind === "spell") return { kind: "spell", id };
  if (kind === "rune") {
    const role = parts[2] === "keystone" || parts[2] === "secondary" ? parts[2] : null;
    return { kind: "rune", id, role };
  }
  return null;
};

const loadPatch = (version: string): Promise<PatchAssets> => {
  const cached = patchCache.get(version);
  if (cached) return cached;
  const pending = Promise.all([
    fetch(`${DDRAGON}/cdn/${version}/data/en_US/item.json`).then(async (res) => {
      if (!res.ok) throw new Error(`item.json ${res.status}`);
      return (await res.json()) as ItemJson;
    }),
    fetch(`${DDRAGON}/cdn/${version}/data/en_US/summoner.json`).then(async (res) => {
      if (!res.ok) throw new Error(`summoner.json ${res.status}`);
      return (await res.json()) as SummonerJson;
    }),
    fetch(`${DDRAGON}/cdn/${version}/data/en_US/runesReforged.json`).then(async (res) => {
      if (!res.ok) throw new Error(`runesReforged.json ${res.status}`);
      return (await res.json()) as RuneStyle[];
    })
  ]).then(([items, summoner, runes]) => ({ items, summoner, runes }));
  patchCache.set(version, pending);
  pending.catch(() => patchCache.delete(version));
  return pending;
};

const decodeHtmlEntities = (value: string): string =>
  value
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");

/** Riot tip tags → hex colours for ClipTextSpan (no Tailwind on the site). */
const TAG_COLOR: Record<string, { color?: string; bold?: boolean }> = {
  attention: { color: "#f0e6d2", bold: true },
  passive: { color: "#c8aa6e", bold: true },
  active: { color: "#c8aa6e", bold: true },
  physicaldamage: { color: "#ff8c4a" },
  magicdamage: { color: "#7b9cff" },
  truedamage: { color: "#f0e6d2" },
  speed: { color: "#f0e6d2" },
  onhit: { color: "#7dd3fc" },
  keyword: { color: "#7dd3fc" },
  spellname: { color: "#7dd3fc" },
  status: { color: "#f0e6d2" },
  raritylegendary: { color: "#c8aa6e" },
  raritymythic: { color: "#c8aa6e" }
};

const fontColor = (raw: string): { color?: string; bold?: boolean } | undefined => {
  const hex = raw.replace(/^#/, "").toLowerCase();
  if (hex === "00ff00" || hex === "0f0" || hex === "1eff00")
    return { color: "#1eff00", bold: true };
  if (hex === "d4af37" || hex === "c8aa6e" || hex === "ffd700")
    return { color: "#c8aa6e", bold: true };
  if (hex === "00b0f0" || hex === "0099ff" || hex === "1e90ff")
    return { color: "#38bdf8", bold: true };
  if (hex === "ff8c00" || hex === "ff8c4a" || hex === "fe445d") return { color: "#ff8c4a" };
  if (hex === "ffffff" || hex === "fff") return {};
  if (/^[0-9a-f]{3,8}$/i.test(hex)) return { color: "#c8aa6e", bold: true };
  return undefined;
};

type Style = { color?: string; bold?: boolean };

/** Parse Data Dragon tip HTML into coloured ClipTextSpan runs. */
export const ddragonHtmlToClipSpans = (html: string): ClipTextSpan[] => {
  const spans: ClipTextSpan[] = [];
  const stack: Style[] = [{}];

  const pushText = (raw: string): void => {
    const text = decodeHtmlEntities(raw);
    if (!text) return;
    const style = stack[stack.length - 1] ?? {};
    const last = spans[spans.length - 1];
    if (
      last &&
      last.color === (style.color ?? null) &&
      Boolean(last.bold) === Boolean(style.bold)
    ) {
      last.text += text;
      return;
    }
    spans.push({
      text,
      color: style.color ?? null,
      bold: style.bold === true
    });
  };

  const tokenRe = /<br\s*\/?>|<\/([a-zA-Z]+)\s*>|<([a-zA-Z]+)([^>]*)\/?>|([^<]+)/gi;
  let match: RegExpExecArray | null;
  while ((match = tokenRe.exec(html)) != null) {
    const full = match[0];
    if (/^<br/i.test(full)) {
      pushText("\n");
      continue;
    }
    if (match[1]) {
      if (stack.length > 1) stack.pop();
      continue;
    }
    if (match[2]) {
      const tag = match[2].toLowerCase();
      const attrs = match[3] ?? "";
      if (tag === "maintext" || tag === "stats" || tag === "rules") {
        stack.push(stack[stack.length - 1] ?? {});
        continue;
      }
      if (tag === "font") {
        const color =
          attrs.match(/color\s*=\s*['"]?\s*#?([A-Fa-f0-9]{3,8})/i)?.[1] ??
          attrs.match(/color\s*=\s*['"]?\s*([A-Za-z]+)/i)?.[1] ??
          "";
        stack.push(fontColor(color) ?? stack[stack.length - 1] ?? {});
        continue;
      }
      stack.push(TAG_COLOR[tag] ?? stack[stack.length - 1] ?? {});
      continue;
    }
    if (match[4]) pushText(match[4]);
  }

  return spans;
};

const buildTip = (parsed: ParsedTipId, assets: PatchAssets): ClipTipCard | null => {
  if (parsed.kind === "item") {
    const info = assets.items.data?.[String(parsed.id)];
    if (!info?.name) return null;
    const total = info.gold?.total ?? 0;
    return {
      title: info.name,
      eyebrow: parsed.quest ? "Quest Reward" : null,
      cost: total > 0 ? { total, base: info.gold?.base ?? 0 } : null,
      body: ddragonHtmlToClipSpans(info.description ?? "")
    };
  }

  if (parsed.kind === "spell") {
    const spell = Object.values(assets.summoner.data ?? {}).find(
      (entry) => Number(entry.key) === parsed.id
    );
    if (!spell?.name) return null;
    return {
      title: spell.name,
      eyebrow: "Summoner spell",
      body: ddragonHtmlToClipSpans(spell.description ?? "")
    };
  }

  for (const style of assets.runes) {
    if (style.id === parsed.id && style.name) {
      return {
        title: style.name,
        eyebrow: parsed.role === "secondary" ? "Secondary tree" : "Rune tree",
        body: []
      };
    }
    for (const slot of style.slots ?? []) {
      for (const rune of slot.runes ?? []) {
        if (rune.id !== parsed.id || !rune.name) continue;
        return {
          title: rune.name,
          eyebrow:
            parsed.role === "keystone"
              ? "Keystone"
              : parsed.role === "secondary"
                ? "Secondary tree"
                : "Rune",
          body: ddragonHtmlToClipSpans(rune.shortDesc ?? "")
        };
      }
    }
  }
  return null;
};

/** Resolve a tipId against a Data Dragon patch. Cached for the page session. */
export const fetchLeagueTip = async (
  tipId: string,
  version: string
): Promise<ClipTipCard | null> => {
  const key = `${version}:${tipId}`;
  if (tipCache.has(key)) return tipCache.get(key) ?? null;
  const parsed = parseClipTipId(tipId);
  if (!parsed || !version) {
    tipCache.set(key, null);
    return null;
  }
  try {
    const assets = await loadPatch(version);
    const tip = buildTip(parsed, assets);
    tipCache.set(key, tip);
    return tip;
  } catch {
    tipCache.set(key, null);
    return null;
  }
};

/**
 * Load a League tip card. Pass `enabled` once the tooltip opens so Data Dragon
 * is only fetched on first hover (results stay in the module cache).
 */
export const useLeagueTip = (
  tipId: string | null | undefined,
  version: string | null | undefined,
  enabled = false
): { tip: ClipTipCard | null; loading: boolean } => {
  const [tip, setTip] = useState<ClipTipCard | null>(() => {
    if (!tipId || !version) return null;
    return tipCache.get(`${version}:${tipId}`) ?? null;
  });
  const [loading, setLoading] = useState(false);

  // Serve the cache and decide whether a fetch is needed during render, so the
  // tip never paints one frame showing the previously hovered card. The effect
  // below is then purely the async fetch (setState inside .then is fine).
  const key = tipId && version ? `${version}:${tipId}` : null;
  const seed = `${key ?? ""}|${enabled}`;
  const [prevSeed, setPrevSeed] = useState(seed);
  if (prevSeed !== seed) {
    setPrevSeed(seed);
    setTip(key ? (tipCache.get(key) ?? null) : null);
    setLoading(key !== null && enabled && !tipCache.has(key));
  }

  useEffect(() => {
    if (!key || !tipId || !version || !enabled) return;
    if (tipCache.has(key)) return;

    let cancelled = false;
    void fetchLeagueTip(tipId, version).then((next) => {
      if (cancelled) return;
      setTip(next);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [key, tipId, version, enabled]);

  return { tip, loading };
};
